import { useEffect, useRef, useState } from 'react'

interface WithIdOrUuid { id?: string; uuid?: string }

export function useLayeredCrossfade<T extends WithIdOrUuid>(
  target: T | null,
  transitionDelay = 250
): {
  topLayer: 'A' | 'B'
  contentA: T | null
  contentB: T | null
} {
  const [topLayer, setTopLayer] = useState<'A' | 'B'>('A')
  const [contentA, setContentA] = useState<T | null>(null)
  const [contentB, setContentB] = useState<T | null>(null)

  const transitionRef = useRef<number | null>(null)
  const topLayerRef = useRef<'A' | 'B'>('A')
  const lastRenderedIdRef = useRef<string | undefined>(undefined)

  const targetId = target?.id ?? target?.uuid

  useEffect(() => {
    if (!target) return

    if (targetId === lastRenderedIdRef.current) {
      return
    }

    const nextLayer = topLayerRef.current === 'A' ? 'B' : 'A'

    if (nextLayer === 'A') {
      setContentA(target)
    } else {
      setContentB(target)
    }

    transitionRef.current = window.setTimeout(() => {
      setTopLayer(nextLayer)
      topLayerRef.current = nextLayer
      lastRenderedIdRef.current = targetId
    }, transitionDelay)

    return () => {
      if (transitionRef.current) clearTimeout(transitionRef.current)
    }
  }, [targetId, transitionDelay])

  return { topLayer, contentA, contentB }
}
